import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { toast } from 'react-toastify';
import styled from 'styled-components';

const STEPS = ['Registration', 'Confirm'];

export default function Register() {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    email: '', last_name: '', first_name: '', password: '', password_confirm: '',
    phone_number: '', membership_category: '', shortcode: '', membership_number: '',
    surcon_number: '', firm_name: '', firm_address: '',
    practice_code: '',
  });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyLogoPreview, setCompanyLogoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [existsModal, setExistsModal] = useState(null); // { blocking, matches: [{field, message}] }
  const { register } = useAuth();
  const { settings: siteSettings } = useSiteSettings();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Photo must be less than 2MB');
        return;
      }
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo must be less than 2MB');
        return;
      }
      setCompanyLogo(file);
      setCompanyLogoPreview(URL.createObjectURL(file));
    }
  };

  const goToStep = (n) => setStep(Math.max(0, Math.min(n, STEPS.length - 1)));

  // Ask the server whether this person already exists before showing the review step.
  const checkExisting = async () => {
    try {
      setChecking(true);
      const res = await api.post('/accounts/register/check/', {
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        shortcode: formData.shortcode,
        membership_number: formData.membership_number,
      });
      return res.data;
    } catch (err) {
      console.warn('Duplicate check unavailable, continuing', err);
      return null; // never block sign-up because the check itself failed
    } finally {
      setChecking(false);
    }
  };

  const nextStep = async () => {
    if (step === 0) {
      if (!formData.last_name || !formData.first_name || !formData.email || !formData.password || !formData.password_confirm) {
        toast.error('Please fill all required fields');
        return;
      }
      if (formData.password.length < 8) {
        toast.error('Password must be at least 8 characters');
        return;
      }
      if (formData.password !== formData.password_confirm) {
        toast.error('Passwords do not match');
        return;
      }
      const check = await checkExisting();
      if (check?.exists) {
        setExistsModal({ blocking: check.blocking, matches: check.matches || [] });
        return; // the popup decides whether to continue
      }
    }
    goToStep(step + 1);
  };

  const prevStep = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Pressing Enter on the first step must only move to the review step, never submit.
    if (step < STEPS.length - 1) {
      nextStep();
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(formData).forEach(([key, val]) => {
        if (val !== '' && val !== null && val !== undefined) {
          fd.append(key, val);
        }
      });
      if (photo) fd.append('photo', photo);
      if (companyLogo) fd.append('company_logo', companyLogo);
      await register(fd);
      toast.success('Registration successful! Please login.');
      navigate('/login');
    } catch (err) {
      console.error('Registration error', err);
      const res = err.response;
      const data = res?.data;
      if (data && typeof data === 'object') {
        const entries = Object.entries(data).map(([field, v]) => ({
          field,
          message: Array.isArray(v) ? v.join(' ') : String(v),
        }));
        const duplicate = entries.some((m) => /already exists/i.test(m.message));
        if (duplicate) {
          setExistsModal({ blocking: true, matches: entries.filter((m) => /already exists/i.test(m.message)) });
        } else {
          toast.error(entries.map((m) => m.message).join(' ') || 'Registration failed. Please check your details and try again.');
        }
      } else if (res) {
        toast.error(`Registration failed (server error ${res.status}). Please try again shortly or contact the secretariat.`);
      } else {
        toast.error('Could not reach the server. Please check your internet connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledWrapper>
      <div className="container">
        <div className="card">
          <img src={siteSettings?.header_logo || '/APPSN.jpg'} alt="APPSN" className="logo" />
          <span className="title">Membership Registration</span>
          <p className="subtitle">Association of Private Practising Surveyors of Nigeria</p>

          {/* Step indicator */}
          <div className="stepper">
            {STEPS.map((label, i) => (
              <div key={label} className={`step-item ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
                <div className="step-circle">{i < step ? '✓' : i + 1}</div>
                <span className="step-label">{label}</span>
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit}>
            {/* Step 0: Registration Fields */}
            {step === 0 && (
              <div className="step-content">
                <div className="section-title">Personal Information</div>
                <div className="photo-upload">
                  <label className="photo-label">Passport Photograph</label>
                  <div className="photo-area">
                    {photoPreview ? (
                      <img src={photoPreview} alt="Preview" className="photo-preview" />
                    ) : (
                      <div className="photo-placeholder">
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#0B5D1E" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="12" cy="10" r="3"/><path d="M6 21v-1a6 6 0 0 1 12 0v1"/></svg>
                        <span>Click to upload</span>
                      </div>
                    )}
                    <input type="file" accept="image/*" onChange={handlePhotoChange} />
                  </div>
                  <span className="photo-hint">Max 2MB — JPG, PNG</span>
                </div>
                <div className="row">
                  <div className="inputBox">
                    <input type="text" name="last_name" required value={formData.last_name} onChange={handleChange} />
                    <span>Surname *</span>
                  </div>
                  <div className="inputBox">
                    <input type="text" name="first_name" required value={formData.first_name} onChange={handleChange} />
                    <span>Other Names *</span>
                  </div>
                </div>
                <div className="row">
                  <div className="inputBox">
                    <input type="tel" name="phone_number" value={formData.phone_number} onChange={handleChange} />
                    <span>Phone Number</span>
                  </div>
                  <div className="inputBox">
                    <select name="membership_category" value={formData.membership_category} onChange={handleChange}>
                      <option value="">— Select Member Status —</option>
                      <option value="fellow">Fellow (FNIS)</option>
                      <option value="corporate">Corporate Member (MNIS)</option>
                      <option value="associate">Associate Member</option>
                      <option value="probationer">Probationer</option>
                      <option value="technician">Technician Member</option>
                    </select>
                    <span className="floated">Member Status</span>
                  </div>
                </div>
                <div className="row">
                  <div className="inputBox">
                    <input type="text" name="shortcode" value={formData.shortcode} onChange={handleChange} />
                    <span>NIS Shortcode</span>
                  </div>
                  <div className="inputBox">
                    <input type="text" name="surcon_number" value={formData.surcon_number} onChange={handleChange} />
                    <span>SURCON No.</span>
                  </div>
                </div>

                <div className="section-title">Company Information</div>
                <div className="inputBox full">
                  <input type="text" name="firm_name" value={formData.firm_name} onChange={handleChange} />
                  <span>Company Name</span>
                </div>
                <div className="photo-upload">
                  <label className="photo-label">Company Logo</label>
                  <div className="photo-area">
                    {companyLogoPreview ? (
                      <img src={companyLogoPreview} alt="Logo Preview" className="photo-preview" />
                    ) : (
                      <div className="photo-placeholder">
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#0B5D1E" strokeWidth="1.5"><rect x="2" y="2" width="20" height="20" rx="3"/><path d="M8 12l3 3 5-5"/></svg>
                        <span>Click to upload</span>
                      </div>
                    )}
                    <input type="file" accept="image/*" onChange={handleLogoChange} />
                  </div>
                  <span className="photo-hint">Max 2MB — JPG, PNG</span>
                </div>
                <div className="inputBox full">
                  <textarea name="firm_address" value={formData.firm_address} onChange={handleChange} rows={2} />
                  <span className={formData.firm_address ? 'floated' : ''}>Company Address</span>
                </div>
                <div className="inputBox full">
                  <input type="text" name="practice_code" value={formData.practice_code} onChange={handleChange} />
                  <span>Practice Code</span>
                </div>

                <div className="section-title">Account</div>
                <div className="inputBox full">
                  <input type="email" name="email" required value={formData.email} onChange={handleChange} />
                  <span>Email Address *</span>
                </div>
                <div className="row">
                  <div className="inputBox">
                    <input type="password" name="password" required minLength={8} value={formData.password} onChange={handleChange} />
                    <span>Password *</span>
                  </div>
                  <div className="inputBox">
                    <input type="password" name="password_confirm" required value={formData.password_confirm} onChange={handleChange} />
                    <span>Confirm Password *</span>
                  </div>
                </div>
              </div>
            )}

            {/* Step 1: Confirmation */}
            {step === 1 && (
              <div className="step-content">
                <div className="section-title">Review &amp; Confirm</div>
                <div className="review-grid">
                  <div className="review-item"><strong>Surname:</strong> {formData.last_name}</div>
                  <div className="review-item"><strong>Other Names:</strong> {formData.first_name}</div>
                  <div className="review-item"><strong>Email:</strong> {formData.email}</div>
                  <div className="review-item"><strong>Phone:</strong> {formData.phone_number || '—'}</div>
                  <div className="review-item"><strong>Member Status:</strong> {formData.membership_category ? {fellow:'Fellow (FNIS)',corporate:'Corporate Member (MNIS)',associate:'Associate Member',probationer:'Probationer',technician:'Technician Member'}[formData.membership_category] || formData.membership_category : '—'}</div>
                  <div className="review-item"><strong>NIS Shortcode:</strong> {formData.shortcode || '—'}</div>
                  <div className="review-item"><strong>NIS Membership ID:</strong> {formData.membership_number || '—'}</div>
                  <div className="review-item"><strong>SURCON No.:</strong> {formData.surcon_number || '—'}</div>
                  <div className="review-item"><strong>Company Name:</strong> {formData.firm_name || '—'}</div>
                  <div className="review-item"><strong>Company Address:</strong> {formData.firm_address || '—'}</div>
                  <div className="review-item"><strong>Practice Code:</strong> {formData.practice_code || '—'}</div>
                  <div className="review-item"><strong>Passport Photo:</strong> {photo ? photo.name : '—'}</div>
                  <div className="review-item"><strong>Company Logo:</strong> {companyLogo ? companyLogo.name : '—'}</div>
                </div>
                <div className="declaration">
                  <p>By registering, I declare that I will be loyal to the Association of Private Practising Surveyors of Nigeria and uphold the ethics and ideals of the profession.</p>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="nav-buttons">
              {step > 0 && (
                <button type="button" className="btn-back" onClick={prevStep}>Back</button>
              )}
              {step < STEPS.length - 1 ? (
                <button type="button" className="btn-next" onClick={nextStep} disabled={checking}>{checking ? 'Checking...' : 'Continue'}</button>
              ) : (
                <button type="submit" className="btn-submit" disabled={loading}>
                  {loading ? 'Submitting...' : 'Submit Registration'}
                </button>
              )}
            </div>
          </form>

          {existsModal && (
            <div role="dialog" aria-modal="true" aria-labelledby="exists-title"
              style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
              onClick={() => setExistsModal(null)}>
              <div onClick={(e) => e.stopPropagation()}
                style={{ background: '#fff', borderRadius: 16, maxWidth: 460, width: '100%', padding: '28px 26px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center' }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', margin: '0 auto 14px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: existsModal.blocking ? '#fee2e2' : '#fef3c7', color: existsModal.blocking ? '#b91c1c' : '#b45309', fontSize: 28, fontWeight: 700 }}>!</div>
                <h3 id="exists-title" style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 800, color: '#111827' }}>User already exists</h3>
                <div style={{ color: '#374151', fontSize: 14, lineHeight: 1.55, marginBottom: 18 }}>
                  {existsModal.matches.map((m, i) => (<p key={i} style={{ margin: '0 0 6px' }}>{m.message}</p>))}
                  <p style={{ margin: '8px 0 0', color: '#6b7280', fontSize: 13 }}>
                    {existsModal.blocking
                      ? 'This person is already registered on the portal. Please sign in, or reset your password if you have forgotten it. If this is not you, contact the secretariat.'
                      : 'If this is you, please sign in instead of registering again. If it is a different person with the same name, you can continue.'}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Link to="/login" style={{ background: '#0B5D1E', color: '#fff', padding: '10px 18px', borderRadius: 10, fontWeight: 700, textDecoration: 'none', fontSize: 14 }}>Sign in</Link>
                  <Link to="/forgot-password" style={{ background: '#f3f4f6', color: '#111827', padding: '10px 18px', borderRadius: 10, fontWeight: 700, textDecoration: 'none', fontSize: 14 }}>Forgot password</Link>
                  {existsModal.blocking ? (
                    <button type="button" onClick={() => setExistsModal(null)}
                      style={{ background: 'transparent', border: '1px solid #d1d5db', color: '#374151', padding: '10px 18px', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>Close</button>
                  ) : (
                    <button type="button" onClick={() => { setExistsModal(null); goToStep(1); }}
                      style={{ background: 'transparent', border: '1px solid #d1d5db', color: '#374151', padding: '10px 18px', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>Not me, continue</button>
                  )}
                </div>
              </div>
            </div>
          )}

          <p className="login-link">
            Already have an account?{' '}
            <Link to="/login">Sign in</Link>
          </p>
        </div>
      </div>
    </StyledWrapper>
  );
}

const StyledWrapper = styled.div`
  .container {
    min-height: 70vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 3rem 1rem;
    background: #F8FAFB;
  }

  .logo {
    width: auto;
    height: 80px;
    object-fit: contain;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .title {
    color: #0B5D1E;
    text-transform: uppercase;
    letter-spacing: 2px;
    display: block;
    font-weight: bold;
    font-size: x-large;
    font-family: 'Poppins', sans-serif;
    text-align: center;
  }

  .subtitle {
    color: #6b7280;
    font-size: 0.875rem;
    margin-top: -15px;
    letter-spacing: 0.5px;
  }

  .card {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 600px;
    max-width: 95vw;
    flex-direction: column;
    gap: 20px;
    background: #e3e3e3;
    box-shadow: 16px 16px 32px #c8c8c8,
          -16px -16px 32px #fefefe;
    border-radius: 12px;
    padding: 2.5rem 2rem;
  }

  /* ---- Stepper ---- */
  .stepper {
    display: flex;
    gap: 8px;
    width: 100%;
    justify-content: center;
  }

  .step-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    max-width: 100px;
  }

  .step-circle {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 2px solid #aaa;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    font-weight: 700;
    color: #aaa;
    background: transparent;
    transition: 0.3s;
  }

  .step-item.active .step-circle {
    border-color: #0B5D1E;
    color: #fff;
    background: #0B5D1E;
  }

  .step-item.done .step-circle {
    border-color: #0B5D1E;
    color: #0B5D1E;
    background: #d4edda;
  }

  .step-label {
    font-size: 0.65rem;
    margin-top: 4px;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 600;
  }

  .step-item.active .step-label {
    color: #0B5D1E;
  }

  /* ---- Form ---- */
  form {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .step-content {
    display: flex;
    flex-direction: column;
    gap: 18px;
    width: 100%;
  }

  .section-title {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 2px;
    color: #0B5D1E;
    font-weight: 700;
    border-bottom: 2px solid #0B5D1E;
    padding-bottom: 6px;
  }

  .row {
    display: flex;
    gap: 16px;
  }

  @media (max-width: 520px) {
    .row {
      flex-direction: column;
    }
  }

  .row > * {
    flex: 1;
  }

  .inputBox {
    position: relative;
  }

  .inputBox.full {
    width: 100%;
  }

  .inputBox input,
  .inputBox textarea {
    width: 100%;
    padding: 12px;
    outline: none;
    border: none;
    color: #000;
    font-size: 1em;
    background: transparent;
    border-left: 2px solid #0B5D1E;
    border-bottom: 2px solid #0B5D1E;
    transition: 0.1s;
    border-bottom-left-radius: 8px;
    font-family: inherit;
    resize: vertical;
  }

  .inputBox span {
    margin-top: 5px;
    position: absolute;
    left: 0;
    transform: translateY(-4px);
    margin-left: 10px;
    padding: 10px;
    pointer-events: none;
    font-size: 12px;
    color: #374151;
    text-transform: uppercase;
    transition: 0.5s;
    letter-spacing: 3px;
    border-radius: 8px;
  }

  .inputBox input:valid ~ span,
  .inputBox input:focus ~ span,
  .inputBox input.has-value ~ span,
  .inputBox textarea:focus ~ span,
  .inputBox span.floated {
    transform: translateX(60px) translateY(-15px);
    font-size: 0.7em;
    padding: 4px 8px;
    background: #0B5D1E;
    letter-spacing: 0.15em;
    color: #fff;
    border: 2px;
  }

  .inputBox input:valid,
  .inputBox input:focus,
  .inputBox textarea:focus {
    border: 2px solid #0B5D1E;
    border-radius: 8px;
  }

  /* ---- Select ---- */
  .selectBox {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .selectBox label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 2px;
    color: #374151;
    font-weight: 600;
  }

  .selectBox select {
    padding: 11px 12px;
    border: 2px solid #0B5D1E;
    border-radius: 8px;
    background: transparent;
    font-size: 0.9rem;
    color: #000;
    outline: none;
    cursor: pointer;
  }

  /* ---- Checkbox ---- */
  .checkboxRow {
    padding-top: 4px;
  }

  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.9rem;
    color: #374151;
    cursor: pointer;
    user-select: none;
  }

  .checkbox-label input[type="checkbox"] {
    accent-color: #0B5D1E;
    width: 18px;
    height: 18px;
    cursor: pointer;
  }

  /* ---- Photo Upload ---- */
  .photo-upload {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .photo-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 2px;
    color: #374151;
    font-weight: 600;
  }

  .photo-area {
    position: relative;
    width: 130px;
    height: 150px;
    border: 2px dashed #0B5D1E;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: 0.3s;
  }

  .photo-area:hover {
    border-color: #1A7A35;
    background: rgba(11, 93, 30, 0.04);
  }

  .photo-area input[type="file"] {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
  }

  .photo-preview {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .photo-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
  }

  .photo-placeholder span {
    font-size: 0.7rem;
    color: #0B5D1E;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .photo-hint {
    font-size: 0.7rem;
    color: #9ca3af;
  }

  /* ---- Review ---- */
  .review-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  @media (max-width: 520px) {
    .review-grid {
      grid-template-columns: 1fr;
    }
  }

  .review-item {
    font-size: 0.85rem;
    color: #374151;
    padding: 6px 0;
    border-bottom: 1px solid #d1d5db;
  }

  .review-item strong {
    color: #0B5D1E;
    font-weight: 600;
  }

  .declaration {
    margin-top: 8px;
    padding: 14px;
    background: rgba(11, 93, 30, 0.06);
    border-left: 3px solid #0B5D1E;
    border-radius: 4px;
  }

  .declaration p {
    font-size: 0.8rem;
    color: #374151;
    line-height: 1.6;
    margin: 0;
  }

  /* ---- Buttons ---- */
  .nav-buttons {
    display: flex;
    justify-content: space-between;
    margin-top: 24px;
    gap: 12px;
  }

  .btn-back,
  .btn-next,
  .btn-submit {
    height: 46px;
    padding: 0 28px;
    border-radius: 8px;
    border: 2px solid #0B5D1E;
    cursor: pointer;
    text-transform: uppercase;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 2px;
    transition: 0.3s;
  }

  .btn-back {
    background: transparent;
    color: #0B5D1E;
  }

  .btn-back:hover {
    background: rgba(11, 93, 30, 0.08);
  }

  .btn-next {
    background: transparent;
    color: #0B5D1E;
    margin-left: auto;
  }

  .btn-next:hover {
    background: #0B5D1E;
    color: #fff;
  }

  .btn-submit {
    background: #0B5D1E;
    color: #fff;
    margin-left: auto;
  }

  .btn-submit:hover {
    background: #094A18;
  }

  .btn-submit:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* ---- Footer link ---- */
  .login-link {
    font-size: 0.875rem;
    color: #6b7280;
    margin-top: -5px;
  }

  .login-link a {
    color: #0B5D1E;
    font-weight: 600;
    text-decoration: none;
    transition: 0.2s;
  }

  .login-link a:hover {
    color: #1A7A35;
  }
`;
